import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS as DndCSS,
} from '@dnd-kit/utilities';
import {
  Edit,
  GripVertical,
  Clock,
  Calendar,
  AlertCircle,
  Sparkles,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Badge } from '../ui/Badge';
import { ListItem, DateScopedListData } from '@/types/lists';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { ItemEditForm } from './ItemEditForm';
import { ItemCreationForm } from './ItemCreationForm';
import { DeleteConfirmModal } from '../shared/DeleteConfirmModal';
import { aiService } from '@/services/aiService';
import { cn } from '@/utils/cn';

interface ItemListProps {
  listId: string;
  items?: ListItem[]; // Now optional - can be derived from contextual data
  contextDate?: Date; // Add context date for universal completion
  onItemUpdate?: (itemId: string, updates: Partial<ListItem>) => void;
  onItemDelete?: (itemId: string) => void;
  onItemReorder?: (itemId: string, newPriority: number) => void;
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  allowEditing?: boolean;
  allowReordering?: boolean;
  className?: string;
  totalItemsCount?: number; // Add total count for accurate display
  completedItemsCount?: number; // Add completed count for accurate display
}

interface SortableItemComponentProps {
  item: ListItem;
  listId: string;
  onToggleComplete: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  allowEditing: boolean;
  showCompleted?: boolean;
  editMode?: boolean; // Controls metadata visibility
}

interface ItemComponentProps {
  item: ListItem;
  listId: string;
  onToggleComplete: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  allowEditing: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
  showPriority?: boolean;
  showCompleted?: boolean;
  editMode?: boolean; // Controls metadata visibility
}

function SortableItemComponent({
  item,
  listId,
  onToggleComplete,
  onEditClick,
  onDeleteClick,
  allowEditing,
  showCompleted = true,
  editMode = false
}: SortableItemComponentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <ItemComponent
        item={item}
        listId={listId}
        onToggleComplete={onToggleComplete}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        allowEditing={allowEditing}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
        showPriority={true}
        showCompleted={showCompleted}
        editMode={editMode}
      />
    </div>
  );
}

function ItemComponent({
  item,
  listId: _listId,
  onToggleComplete,
  onEditClick,
  onDeleteClick,
  allowEditing,
  isDragging,
  dragHandleProps,
  showPriority = false,
  showCompleted = true,
  editMode = false
}: ItemComponentProps) {
  const isCompleted = 'completed' in item && (item as any).completed;

  // Hide completed items when showCompleted is false
  if (isCompleted && !showCompleted) {
    return null;
  }

  const isOverdue = (() => {
    // Note: completed status is now contextual - this function is used for display in template mode
    if (!item.dueDate) return false;
    try {
      const dueDate = new Date(item.dueDate);
      return !isNaN(dueDate.getTime()) && dueDate < new Date();
    } catch {
      return false;
    }
  })();


  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };


  return (
    <div
      className={cn(
        'group p-3 rounded-lg border transition-all duration-200',
        'hover:border-gray-400',
        ('completed' in item && (item as any).completed) && 'opacity-60',
        isOverdue && (!('completed' in item) || !(item as any).completed) && 'border-red-200',
        isDragging && 'opacity-50 transform rotate-1 shadow-lg z-50'
      )}
    >
      {/* Edit mode: Actions at top */}
      {editMode && allowEditing && (
        <div className="flex items-center justify-end gap-1 mb-2">
          {/* Edit Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
            className="entity-card-action-btn"
          >
            <Edit className="h-4 w-4" />
          </Button>

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick();
            }}
            className="entity-card-delete-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          {/* Drag Handle */}
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="entity-card-drag-handle"
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      <div className="item-card-row">
        {/* Content area */}
        <div className="item-card-content">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="space-y-1">
              {/* Priority - only show in edit mode */}
              {editMode && showPriority && (
                <span className="text-xs text-gray-400">#{item.priority}</span>
              )}

              {/* Title - always visible */}
              <div className={cn(
                'font-medium text-sm',
                ('completed' in item && (item as any).completed) && 'line-through text-gray-500'
              )}>
                {item.content}
              </div>

              {/* Description - only in edit mode */}
              {editMode && item.description && (
                <p className="text-xs text-gray-600">{item.description}</p>
              )}

              {/* Metadata - only in edit mode */}
              {editMode && (item.estimatedMinutes || item.dueDate) && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {/* Estimated time */}
                  {item.estimatedMinutes && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {item.estimatedMinutes}m
                    </span>
                  )}

                  {/* Due date */}
                  {item.dueDate && (
                    <span className={cn(
                      "flex items-center gap-1",
                      isOverdue ? "text-red-600" : "text-gray-500"
                    )}>
                      <Calendar className="h-3 w-3" />
                      {(() => {
                        try {
                          const dueDate = new Date(item.dueDate);
                          if (isNaN(dueDate.getTime())) {
                            return 'Invalid date';
                          }
                          return new Intl.DateTimeFormat('en-US', {
                            month: 'short',
                            day: 'numeric'
                          }).format(dueDate);
                        } catch (error) {
                          console.error('Date formatting error:', error, item.dueDate);
                          return 'Invalid date';
                        }
                      })()}
                      {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Badges - only in edit mode */}
          {editMode && item.difficulty && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Difficulty Badge */}
              <Badge variant="outline" className={cn('text-xs px-1 py-0', getDifficultyColor(item.difficulty))}>
                {item.difficulty}
              </Badge>
            </div>
          )}
        </div>

        {/* Checkbox - always at bottom right */}
        <div className="item-card-actions">
          <Checkbox
            checked={'completed' in item ? (item as any).completed : false}
            onCheckedChange={onToggleComplete}
            className="flex-shrink-0 w-8 h-8"
          />
        </div>
      </div>
    </div>
  );
}

export function ItemList({
  listId,
  items: providedItems,
  contextDate = new Date(),
  onItemUpdate,
  onItemReorder,
  showCompleted = true,
  onToggleShowCompleted,
  allowEditing = true,
  allowReordering = true,
  className,
  totalItemsCount,
  completedItemsCount
}: ItemListProps) {
  const [editingItem, setEditingItem] = useState<ListItem | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAICreationForm, setShowAICreationForm] = useState(false);
  const [listData, setListData] = useState<DateScopedListData | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ListItem | null>(null);
  
  // Optimistic update state for immediate UI feedback
  const [optimisticItems, setOptimisticItems] = useState<ListItem[]>([]);
  const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false);
  
  const {
    lists,
    reorderItemsArray,
    updateItemCompletion,
    getListDataForContext,
    deleteItem
  } = useListsStore();

  const { getGoalById } = useGoalsStore();

  // Get contextual list data
  useEffect(() => {
    const data = getListDataForContext(listId, contextDate);
    setListData(data);
  }, [listId, contextDate, getListDataForContext, lists]);
  
  // Clear optimistic updates when store data changes
  useEffect(() => {
    if (lists && isOptimisticUpdate) {
      // Store has been updated, clear optimistic state
      setIsOptimisticUpdate(false);
      setOptimisticItems([]);
      console.log('🔄 Clearing optimistic updates - store data updated');
    }
  }, [lists, isOptimisticUpdate]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔄 Items Drag End Event:', { activeId: active.id, overId: over?.id });

    if (over && active.id !== over.id) {
      const currentItems = listData ? listData.items : providedItems || [];
      const oldIndex = currentItems.findIndex(item => item.id === active.id);
      const newIndex = currentItems.findIndex(item => item.id === over.id);
      
      console.log('📊 Items Drag Details:', {
        activeId: active.id,
        overId: over.id,
        oldIndex,
        newIndex,
        originalOrder: currentItems.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
      });

      // Create visual reorder for immediate UI feedback
      const visuallyReordered = arrayMove(currentItems, oldIndex, newIndex);
      
      // Use sequential priorities like Goals and Lists do (matching working pattern)
      const finalReordered = visuallyReordered.map((item, index) => ({
        ...item,
        priority: index + 1, // Sequential priorities starting from 1
        updatedAt: new Date()
      }));
      
      const draggedItem = currentItems[oldIndex];
      console.log('🎯 Items Priority Recalculation:', {
        draggedItem: draggedItem.content,
        oldPriority: draggedItem.priority,
        oldIndex,
        newIndex,
        finalOrder: finalReordered.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
      });
      
      console.log('🔀 Final Items Order with Sequential Priorities:', 
        finalReordered.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
      );

      // Show optimistic update immediately for smooth UX
      setOptimisticItems(finalReordered);
      setIsOptimisticUpdate(true);
      console.log('🚀 Showing optimistic update immediately');
      
      // Then sync with server (cache invalidation will happen automatically)
      if (onItemReorder) {
        // Use callback pattern if provided
        onItemReorder(draggedItem.id, newIndex + 1);
        // Clear optimistic state immediately for callback pattern
        setIsOptimisticUpdate(false);
        setOptimisticItems([]);
      } else {
        // Use store method with proper cache invalidation
        reorderItemsArray(listId, finalReordered).then(() => {
          // Cache invalidation happened in service, optimistic updates will clear automatically
          console.log('✅ Items reordered and cache invalidated');
        }).catch(error => {
          // Revert optimistic update on error
          setIsOptimisticUpdate(false);
          setOptimisticItems([]);
          console.error('❌ Failed to reorder items, reverted:', error);
        });
      }
    } else {
      console.log('❌ No items reorder needed:', { 
        sameItem: active.id === over?.id, 
        noOver: !over 
      });
    }
  };

  const handleToggleComplete = async (item: ListItem | (ListItem & { completed: boolean })) => {
    try {
      const currentCompleted = 'completed' in item ? item.completed : false;
      await updateItemCompletion(listId, item.id, !currentCompleted, contextDate);
      
      // Refresh list data
      const updatedData = getListDataForContext(listId, contextDate);
      setListData(updatedData);
      
      // Also dispatch a custom event to notify other components (like Calendar)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('item-completion-changed', {
          detail: { listId, itemId: item.id, completed: !currentCompleted, contextDate }
        }));
        console.log('🔔 ItemList: Dispatched item-completion-changed event');
      }
      
      onItemUpdate?.(item.id, { /* no longer using completed in updates */ });
    } catch (error) {
      console.error('Failed to toggle item completion:', error);
    }
  };

  // New handler for edit form
  const handleEditClick = (item: ListItem) => {
    setEditingItem(item);
    setShowEditForm(true);
  };

  const handleEditFormClose = () => {
    setEditingItem(null);
    setShowEditForm(false);
  };

  // Delete handlers
  const handleDeleteClick = (item: ListItem) => {
    setItemToDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteItem(listId, itemToDelete.id);
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Use optimistic updates when available, otherwise contextual data or provided items
  const items = isOptimisticUpdate ? optimisticItems : 
               (listData ? listData.items : providedItems || []);
  
  console.log('🔍 ItemList - Data sources:', {
    isOptimisticUpdate,
    hasListData: !!listData,
    listDataItems: listData?.items?.length || 0,
    providedItems: providedItems?.length || 0,
    finalItemsCount: items.length,
    finalItemsOrder: items.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
  });
  
  // Use contextual data if available, otherwise provided totals, otherwise calculate from visible items
  const completedCount = listData ? listData.completedItems : 
    (completedItemsCount !== undefined ? completedItemsCount : items.filter(item => 'completed' in item && item.completed).length);
  const totalCount = listData ? listData.totalItems : 
    (totalItemsCount !== undefined ? totalItemsCount : items.length);
  const progressPercentage = listData ? listData.progress : 
    (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0);
  
  // Only allow reordering when all items are visible (showCompleted = true or no completed items)
  const canReorder = allowReordering && showCompleted;

  if (!listData && !providedItems) {
    return <div>Loading...</div>;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Progress Summary with Menu */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium">{completedCount}</span> of{' '}
          <span className="font-medium">{totalCount}</span> items completed{' '}
          <span className="text-gray-400">({progressPercentage}%)</span>
        </div>
        
        {/* Options Menu */}
        <div className="flex items-center gap-2">
          {/* AI Item Creation Button */}
          {aiService.isConfigured() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAICreationForm(true)}
              className="h-8 w-8 p-0"
              title="Generate items with AI"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
          )}
          
          {/* Edit Mode Toggle - Show/Hide Completed Items and Edit Buttons */}
          {onToggleShowCompleted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleShowCompleted}
              className={cn(
                "h-8 w-8 p-0 transition-colors",
                showCompleted && "bg-blue-100 text-blue-600 hover:bg-blue-200"
              )}
              title={showCompleted ? "Exit edit mode" : "Enter edit mode"}
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Items List */}
      {canReorder ? (
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={items.map(i => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No items yet
                </div>
              ) : (
                items.map((item) => (
                  <SortableItemComponent
                    key={item.id}
                    item={item}
                    listId={listId}
                    onToggleComplete={() => handleToggleComplete(item)}
                    onEditClick={() => handleEditClick(item)}
                    onDeleteClick={() => handleDeleteClick(item)}
                    allowEditing={allowEditing}
                    showCompleted={showCompleted}
                    editMode={showCompleted}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No items yet
            </div>
          ) : (
            items.map((item) => (
              <ItemComponent
                key={item.id}
                item={item}
                listId={listId}
                onToggleComplete={() => handleToggleComplete(item)}
                onEditClick={() => handleEditClick(item)}
                onDeleteClick={() => handleDeleteClick(item)}
                allowEditing={allowEditing}
                showPriority={false}
                showCompleted={showCompleted}
                editMode={showCompleted}
              />
            ))
          )}
        </div>
      )}

      {/* Edit Form Modal */}
      {editingItem && (
        <ItemEditForm
          isOpen={showEditForm}
          onClose={handleEditFormClose}
          listId={listId}
          item={editingItem}
        />
      )}

      {/* AI Item Creation Form */}
      {(() => {
        // Find the list to get the linked goal
        const list = lists.find(l => l.id === listId);
        const linkedGoal = list?.goalId ? getGoalById(list.goalId) : null;

        if (!list || !linkedGoal) return null;

        return (
          <ItemCreationForm
            isOpen={showAICreationForm}
            onClose={() => setShowAICreationForm(false)}
            list={list}
            linkedGoal={linkedGoal}
            existingItems={items}
          />
        );
      })()}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={itemToDelete !== null}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Item"
        itemName={itemToDelete?.content || ''}
        itemType="item"
        description="This action cannot be undone."
      />
    </div>
  );
}